/* eslint-disable react-refresh/only-export-components -- exports the useAuth hook alongside the provider */
import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { analytics } from 'virtual:hp-heycatch';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  // Bump to force a profile refetch (e.g. after this client updates the row).
  const [profileVersion, setProfileVersion] = useState(0);
  const [loading, setLoading] = useState(!!supabase);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBrainstormBeta, setIsBrainstormBeta] = useState(false);
  const mountedRef = useRef(true);

  // Track auth state only — no API calls inside the callback
  useEffect(() => {
    if (!supabase) {
      return;
    }

    mountedRef.current = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mountedRef.current) return;

      if (session?.user) {
        // Only update if user actually changed (avoids re-renders on TOKEN_REFRESHED)
        setCurrentUser(prev => {
          if (prev?.id === session.user.id) return prev;
          return session.user;
        });
      } else {
        setCurrentUser(null);
        setUserProfile(null);
        setIsAdmin(false);
        setIsBrainstormBeta(false);
        setLoading(false);
      }
      // Only on a real sign-out: the initial no-session state must keep the
      // anonymous visitor, so a later sign-up joins their landing-page visit.
      if (event === 'SIGNED_OUT') analytics.resetIdentity();
    });

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  }, []);

  // HeyCatch identity (Hollerpost build only; a no-op elsewhere). Once per
  // signed-in user, not on token refreshes. Supabase's user id is the stable
  // id; email and name go in the properties so the dashboard shows a person.
  useEffect(() => {
    if (!currentUser) return;
    const name = currentUser.user_metadata?.full_name;
    analytics.setIdentity(
      currentUser.id,
      { email: currentUser.email, ...(name ? { name } : {}) },
      currentUser.created_at ? { signup_date: currentUser.created_at } : undefined,
    );
  }, [currentUser]);

  // Fetch profile whenever user changes — separate from auth callback
  useEffect(() => {
    if (!supabase || !currentUser) {
      return;
    }

    let cancelled = false;

    const fetchProfile = async () => {
      // Reset derived flags before the async call so a fetch error never
      // leaves a stale value from a prior account in place.
      setUserProfile(null);
      setIsAdmin(false);
      setIsBrainstormBeta(false);

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', currentUser.id)
          .single();

        if (cancelled) return;

        if (error) {
          console.error('Error fetching profile:', error);
        } else {
          setUserProfile(data);
          setIsAdmin(data?.role === 'admin');
          setIsBrainstormBeta(data?.brainstorm_beta_enabled === true);
        }
      } catch (err) {
        console.error('Unexpected error fetching profile:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProfile();

    return () => { cancelled = true; };
  }, [currentUser, profileVersion]);

  // captchaToken: Turnstile token when Supabase captcha protection is on
  // (VITE_TURNSTILE_SITE_KEY set); omitted otherwise, so calls are unchanged.
  const login = async (email, password, { captchaToken } = {}) => {
    if (!supabase) return { success: false, error: 'Auth not configured' };
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        ...(captchaToken ? { options: { captchaToken } } : {}),
      });

      if (error) throw error;

      return { success: true, user: data.user };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    if (!supabase) return { success: false, error: 'Auth not configured' };
    try {
      // 'local' ends only this browser's session. The default ('global')
      // revokes every session for the account, so signing out of
      // app.hollerpost.com left a dead token in an open utopiancreations.one
      // admin tab (worker calls then 401 with session_not_found).
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  };

  const signup = async (email, password, { fullName, businessName, emailRedirectTo, captchaToken } = {}) => {
    if (!supabase) return { success: false, error: 'Auth not configured' };
    try {
      // business_name rides in user_metadata (persisted on the auth.users row
      // server-side, not just this browser) so it survives an email-
      // confirmation redirect even on a different device — bootstrap-workspace
      // reads it back via supabase.auth.getUser() once a session exists.
      const metadata = {
        ...(fullName ? { full_name: fullName } : {}),
        ...(businessName ? { business_name: businessName } : {}),
      };
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          ...(Object.keys(metadata).length ? { data: metadata } : {}),
          ...(emailRedirectTo ? { emailRedirectTo } : {}),
          ...(captchaToken ? { captchaToken } : {}),
        },
      });

      if (error) throw error;
      // session is null when the project requires email confirmation.
      return { success: true, user: data.user, session: data.session };
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: error.message };
    }
  };

  const refreshProfile = () => setProfileVersion((v) => v + 1);

  // Alias for backward compatibility
  const register = signup;

  const value = {
    // New Standard
    currentUser,
    userProfile,

    // Existing/Compatibility
    user: currentUser,
    isAdmin,
    isBrainstormBeta,
    loading,

    // Actions
    refreshProfile,
    login,
    logout,
    signup,
    register
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
